/**
 * /components/:widgetId/branding: the branding editor. Settings on the left, the
 * live preview (the real widget renderer) on the right. Edits apply to the
 * preview instantly and are saved with the Save button in the widget header.
 */
import { useEffect, useMemo, useState } from "react";
import { RotateCcw } from "lucide-react";
import { LIMITS, normaliseBranding, SOLAR_DEFAULT_HEIGHT, SOLAR_MIN_HEIGHT, type WidgetBranding } from "@widget";
import { useApi } from "../../api";
import { ColorField, Field, FontPicker, Section, Segmented, Slider, Toggle } from "../../components/controls";
import { WidgetPreview } from "../../components/WidgetPreview";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { LogoUpload } from "../../components/LogoUpload";
import { brandingFor } from "./NewWidgetPage";
import { useSaveBar, useTabDraft, useWidget } from "./WidgetLayout";
import "../../editor.css";

// Labels for the colour settings, in the order they're shown
const COLOR_FIELDS: [keyof WidgetBranding["colors"], string][] = [
  ["primary", "Buttons & highlights"],
  ["primaryText", "Button text"],
  ["background", "Background"],
  ["text", "Text"],
  ["mutedText", "Secondary text"],
  ["border", "Borders"],
  ["inputBackground", "Input background"],
  ["accent", "Quote highlight"],
];

// Editable wording: [key, label, multi-line]
const COPY_FIELDS: [keyof WidgetBranding["copy"], string, boolean][] = [
  ["title", "Heading", false],
  ["subtitle", "Intro text", true],
  ["buttonText", "Postcode button", false],
  ["quoteTitle", "Quote heading", false],
  ["disclaimer", "Quote disclaimer", true],
  ["consentText", "Consent wording (GDPR)", true],
  ["thankYouTitle", "Thank-you heading", false],
  ["thankYouText", "Thank-you text", true],
];

// A solar estimate's wording (docs/12 §8): address search, then the roof result
const SOLAR_COPY_FIELDS: [keyof WidgetBranding["copy"], string, boolean][] = [
  ["title", "Heading", false],
  ["subtitle", "Intro text", true],
  ["buttonText", "Search button", false],
  ["quoteTitle", "Result heading ({n} = panels that fit)", false],
  ["disclaimer", "Price disclaimer", true],
  ["consentText", "Consent wording (GDPR)", true],
  ["thankYouTitle", "Thank-you heading", false],
  ["thankYouText", "Thank-you text", true],
];

// A contact form's wording: no postcode search or quote screen
const CONTACT_COPY_FIELDS: [keyof WidgetBranding["copy"], string, boolean][] = [
  ["title", "Heading", false],
  ["subtitle", "Intro text", true],
  ["buttonText", "Send button", false],
  ["consentText", "Consent wording (GDPR)", true],
  ["thankYouTitle", "Thank-you heading", false],
  ["thankYouText", "Thank-you text", true],
];

export function BrandingPage() {
  const { widget, setWidget } = useWidget();
  const api = useApi();
  const saved = useMemo(() => normaliseBranding(widget.branding), [widget.branding]);
  // Kept by the layout, so unsaved edits survive switching tabs
  const [draft, setDraft, discardDraft] = useTabDraft<WidgetBranding>("branding", saved);
  const [saving, setSaving] = useState(false);
  // Reset-to-defaults waits for confirmation in a dialog
  const [confirmReset, setConfirmReset] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  // Warn before leaving the page with unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /** Patch one group of settings, e.g. set("colors", { primary: "#000000" }). */
  function set<K extends Exclude<keyof WidgetBranding, "logoUrl">>(group: K, patch: Partial<WidgetBranding[K]>) {
    setDraft((d) => ({ ...d, [group]: { ...d[group], ...patch } }));
    setMessage(null);
  }

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      setWidget(await api.updateWidget(widget.widgetId, { branding: draft }));
      discardDraft();
      setMessage({ tone: "success", text: "Saved. Your live calculator updates within a minute." });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  // Save / Discard live in the widget header
  useSaveBar({ dirty, saving, blocked: false, onSave: save, onDiscard: () => setDraft(saved) });

  const { layout, shape, colors, copy } = draft;
  const isFull = layout.style === "full" || layout.style === "hosted";
  const floating = !isFull && layout.mode === "floating";

  return (
    <div className="editor">
      <ConfirmDialog
        open={confirmReset}
        title="Reset the styling?"
        confirmLabel="Reset to defaults"
        onConfirm={() => {
          // Solar estimates reset to their own wording and launcher label
          setDraft(brandingFor(draft.layout.style, widget.product));
          setConfirmReset(false);
        }}
        onCancel={() => setConfirmReset(false)}
      >
        <p>Every branding setting on this page goes back to the default. Nothing is saved until you press Save, so you can still discard.</p>
      </ConfirmDialog>
      {/* ── Settings ─────────────────────────────────────────────────────── */}
      <aside className="editor-panel">
        {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}

        <Section title="Layout">
          {/* Compact: the small card. Full width: the quote page with property details,
              grant check, EPC card and a progress sidebar. Full width always sits in the
              page at full width, so it has no placement or alignment options. */}
          {/* The style is chosen when the widget is created and can't change here */}
          <Field label={layout.style === "contact" ? "Component" : "Calculator style"}>
            <p className="ed-fixed">{layout.style === "contact" ? "Contact form (compact card)" : layout.style === "hosted" ? "Hosted page (full width)" : isFull ? "Full width" : "Compact"}
              {widget.product === "solar" ? " solar estimate" : ""}</p>
          </Field>
          {isFull && <p className="muted ed-note">Full width fills the width of the space you put it in (up to 1,200 px).</p>}
          {!isFull && (
          <Field label="Placement">
            <Segmented
              value={layout.mode}
              options={[
                ["inline", "In the page"],
                ["floating", "Floating button"],
              ]}
              onChange={(mode) => set("layout", { mode })}
            />
          </Field>
          )}
          {!isFull && !floating && (
            <>
              <Field label="Alignment">
                <Segmented
                  value={layout.align}
                  options={[
                    ["left", "Left"],
                    ["center", "Centre"],
                    ["right", "Right"],
                  ]}
                  onChange={(align) => set("layout", { align })}
                />
              </Field>
              <Toggle id="full-width" checked={layout.fullWidth} label="Fill the container's width" onChange={(fullWidth) => set("layout", { fullWidth })} />
            </>
          )}
          {!isFull && floating && (
            <>
              <Field label="Corner">
                <Segmented
                  value={layout.position}
                  options={[
                    ["bottom-left", "Bottom left"],
                    ["bottom-right", "Bottom right"],
                  ]}
                  onChange={(position) => set("layout", { position })}
                />
              </Field>
              <Field label="Distance from side" htmlFor="offset-x">
                <Slider id="offset-x" value={layout.offsetX} min={LIMITS.offset[0]} max={LIMITS.offset[1]} onChange={(offsetX) => set("layout", { offsetX })} />
              </Field>
              <Field label="Distance from bottom" htmlFor="offset-y">
                <Slider id="offset-y" value={layout.offsetY} min={LIMITS.offset[0]} max={LIMITS.offset[1]} onChange={(offsetY) => set("layout", { offsetY })} />
              </Field>
              <Field label="Launcher button text" htmlFor="launcher">
                <input id="launcher" className="input" maxLength={40} value={layout.launcherLabel} onChange={(e) => set("layout", { launcherLabel: e.target.value })} />
              </Field>
            </>
          )}
        </Section>

        <Section title="Canvas size">
          {/* Solar: the map needs a fixed height (it can't grow with content), so it's always set */}
          {widget.product === "solar" && (
            <Field label="Map height" htmlFor="map-height" hint="How tall the roof map is on your page. It never grows taller than the visitor's screen.">
              <Slider id="map-height" value={layout.height ?? SOLAR_DEFAULT_HEIGHT} min={SOLAR_MIN_HEIGHT} max={LIMITS.height[1]} step={10} onChange={(height) => set("layout", { height })} />
            </Field>
          )}
          {/* Full width and hosted fill the space they're placed in (up to 1200px), so only padding applies */}
          {!isFull && (
            <>
          <Field label={!floating && layout.fullWidth ? "Width (fills container)" : "Width"} htmlFor="width">
            <Slider id="width" value={layout.width} min={LIMITS.width[0]} max={LIMITS.width[1]} step={10} disabled={!floating && layout.fullWidth} onChange={(width) => set("layout", { width })} />
          </Field>
          <Toggle
            id="auto-height"
            checked={layout.height === null}
            label="Height grows with the content"
            onChange={(auto) => set("layout", { height: auto ? null : 600 })}
          />
          {layout.height !== null && (
            <Field label="Fixed height" htmlFor="height" hint="Content scrolls inside the canvas if it's taller.">
              <Slider id="height" value={layout.height} min={LIMITS.height[0]} max={LIMITS.height[1]} step={10} onChange={(height) => set("layout", { height })} />
            </Field>
          )}
            </>
          )}
          <Field label="Inner padding" htmlFor="padding">
            <Slider id="padding" value={layout.padding} min={LIMITS.padding[0]} max={LIMITS.padding[1]} onChange={(padding) => set("layout", { padding })} />
          </Field>
        </Section>

        <Section title="Font">
          <Field label="Google Font" hint="Used for everything in the calculator.">
            <FontPicker value={draft.font.family} onChange={(family) => set("font", { family })} />
          </Field>
        </Section>

        <Section title="Colours">
          <div className="ed-colors">
            {COLOR_FIELDS.map(([key, label]) => (
              <ColorField key={key} id={`color-${key}`} label={label} value={colors[key]} onChange={(v) => set("colors", { [key]: v })} />
            ))}
          </div>
        </Section>

        <Section title="Shape">
          <Field label="Canvas corners" htmlFor="radius">
            <Slider id="radius" value={shape.radius} min={LIMITS.radius[0]} max={LIMITS.radius[1]} onChange={(radius) => set("shape", { radius })} />
          </Field>
          <Field label="Button & input corners" htmlFor="button-radius">
            <Slider id="button-radius" value={shape.buttonRadius} min={LIMITS.buttonRadius[0]} max={LIMITS.buttonRadius[1]} onChange={(buttonRadius) => set("shape", { buttonRadius })} />
          </Field>
          <Field label="Shadow">
            <Segmented
              value={shape.shadow}
              options={[
                ["none", "None"],
                ["soft", "Soft"],
                ["strong", "Strong"],
              ]}
              onChange={(shadow) => set("shape", { shadow })}
            />
          </Field>
          <Toggle id="canvas-border" checked={shape.border} label="Border around the calculator" onChange={(border) => set("shape", { border })} />
        </Section>

        <Section title="Logo" defaultOpen={false}>
          <Field label="Logo" hint="Shown at the top of the calculator. PNG, JPEG or WebP, up to 1 MB.">
            <LogoUpload widgetId={widget.widgetId} value={draft.logoUrl} onChange={(logoUrl) => setDraft((d) => ({ ...d, logoUrl }))} />
          </Field>
        </Section>

        <Section title="Wording" defaultOpen={false}>
          {(widget.product === "solar" ? SOLAR_COPY_FIELDS : layout.style === "contact" ? CONTACT_COPY_FIELDS : COPY_FIELDS).map(([key, label, multi]) => (
            <Field key={key} label={label} htmlFor={`copy-${key}`}>
              {multi ? (
                <textarea id={`copy-${key}`} className="input ed-textarea" rows={3} value={copy[key]} onChange={(e) => set("copy", { [key]: e.target.value })} />
              ) : (
                <input id={`copy-${key}`} className="input" value={copy[key]} onChange={(e) => set("copy", { [key]: e.target.value })} />
              )}
            </Field>
          ))}
        </Section>

        {/* Back to the defaults for this widget's style (asks first) */}
        <div className="editor-reset">
          <button type="button" className="btn btn-secondary" onClick={() => setConfirmReset(true)}>
            <RotateCcw size={15} aria-hidden="true" /> Reset to default styling
          </button>
        </div>

        <p className="ed-hint ed-powered">"Powered by xenscope" is always shown at the bottom of the calculator.</p>
      </aside>

      {/* ── Live preview ─────────────────────────────────────────────────── */}
      <section className="editor-preview" aria-label="Live preview">
        <WidgetPreview branding={draft} pricing={widget.pricing} product={widget.product} widgetId={widget.widgetId} />
      </section>
    </div>
  );
}
